import { ORDER_HISTORY_DEFAULT_PAGE_SIZE } from '../constants/pagination.js';
import OrderActionSummaryDto, { ItemAction } from '../types/orderActionSummary';
import { OrderDetails } from '../types/orderDetails';
import {
  GetParams,
  dispatchGetRequest,
  dispatchPostRequest,
} from '../util/apiUtils';
import {
  trackAnonymousOrderTransferError,
  trackAnonymousOrderTransferRequested,
  trackAnonymousOrderTransferSuccess,
  trackFetchAnonymousOrderRequested,
  trackFetchOrderError,
  trackGetSupportedOrderActionsError,
} from './newRelic/bopNewRelicTrackingApi';

const prodBopBaseUrl = process.env.PRODUCTION_BOP_BASE_URL || '';
const stagingBopBaseUrl = process.env.STAGING_BOP_BASE_URL || '';

/**
 * Gets the base url for the BOP service
 * @param {string} environment The environment to send BOP service requests too.
 */
const getBOPBaseUrl = (environment: string): string => {
  if (!environment) {
    console.error(
      `Unable to derive service config for a null/empty environment, using STAGING environment as sensible fallback.`
    );
    return stagingBopBaseUrl;
  }
  switch (environment) {
    case 'PRODUCTION':
      return prodBopBaseUrl;
    case 'STAGING':
      return stagingBopBaseUrl;
    default:
      console.warn(
        `Unsupported environment type of ${environment}, using STAGING environment as sensible fallback.`
      );
      return stagingBopBaseUrl;
  }
};

type GetSupportedOrderActionsParams = {
  tenantEnvironment: string;
  shopperId: string;
  orderNumber: string;
};

export const getSupportedOrderActions = async ({
  tenantEnvironment,
  orderNumber,
  shopperId,
}: GetSupportedOrderActionsParams): Promise<ItemAction[]> => {
  const baseUrl = getBOPBaseUrl(tenantEnvironment);
  const url = `${baseUrl}/shopper/${shopperId}/order/${orderNumber}/supportedOrderActions`;
  const response = await dispatchGetRequest({ url });
  if (response.status !== 200) {
    trackGetSupportedOrderActionsError(shopperId, orderNumber);
    throw new Error('request failed');
  }
  const { supportedItemActions } =
    (await response.json()) as OrderActionSummaryDto;
  return supportedItemActions;
};

type GetReorderableItemsParams = {
  shopperId: string;
  environment: string;
  vistacartTenantName: string;
  page: number;
  pageSize: number;
  includeRetired?: boolean;
};

export const getReorderableItems = async ({
  shopperId,
  environment,
  vistacartTenantName,
  page,
  pageSize,
  includeRetired = false,
}: GetReorderableItemsParams) => {
  const baseUrl = getBOPBaseUrl(environment);
  const params: GetParams = { page, pageSize, includeRetired };
  const url = `${baseUrl}/reorderableItems/${vistacartTenantName}/shopper/${shopperId}`;
  const response = await dispatchGetRequest({ url, params });
  if (response.status !== 200) {
    throw new Error('request failed');
  }
  const data = await response.json();
  return data;
};

type GetOrderDetailsParams = {
  orderId: string;
  environment: string;
  shopperId: string;
};

export const getOrderDetails = async ({
  orderId,
  environment,
  shopperId,
}: GetOrderDetailsParams): Promise<OrderDetails> => {
  const baseUrl = getBOPBaseUrl(environment);
  const url = `${baseUrl}/ui/shopper/${shopperId}/orders/${orderId}`;
  try {
    const response = await dispatchGetRequest({ url });
    const orderDetails = (await response.json()) as OrderDetails;
    return orderDetails;
  } catch (err) {
    trackFetchOrderError(err as object, orderId);
    throw err;
  }
};

type GetAnonymousOrderDetailsParams = {
  email: string;
  orderNumber: string;
  environment: string;
  vistacartTenant: string;
};

export const getAnonymousOrderDetails = async ({
  email,
  orderNumber,
  environment,
  vistacartTenant,
}: GetAnonymousOrderDetailsParams) => {
  const baseUrl = getBOPBaseUrl(environment);
  const url = `${baseUrl}/ui/email/${email}/orders/${orderNumber}`;

  try {
    const response = await dispatchGetRequest({ url });
    const metricsData = {
      orderNumber,
      tenantId: vistacartTenant,
    };
    trackFetchAnonymousOrderRequested(metricsData);
    const data = await response.json();
    return data;
  } catch (err) {
    trackFetchOrderError(err as object, orderNumber);
    throw err;
  }
};

type GetOrderSummaryParams = {
  environment: string;
  offset: number;
  limit: number;
  vistacartTenantName: string;
  shopperId: string;
};

export const getOrdersSummary = async ({
  environment,
  offset = 0,
  limit = ORDER_HISTORY_DEFAULT_PAGE_SIZE,
  vistacartTenantName,
  shopperId,
}: GetOrderSummaryParams) => {
  const baseUrl = getBOPBaseUrl(environment);
  const params: GetParams = {
    offset,
    limit,
    tenantId: vistacartTenantName,
  };
  const url = `${baseUrl}/ui/shopper/${shopperId}/orders/summaries`;
  try {
    const response = await dispatchGetRequest({ url, params });
    const data = await response.json();
    return data;
  } catch (err) {
    trackFetchOrderError(err as object, '');
    throw err;
  }
};

type ChangeOrderOwnerParams = {
  orderNumber: string;
  shopperId: string;
  environment: string;
};

export const changeOrderOwner = async ({
  orderNumber,
  shopperId,
  environment,
}: ChangeOrderOwnerParams) => {
  const metricsData = {
    orderNumber,
    shopperId,
    environment,
  };

  trackAnonymousOrderTransferRequested(metricsData);
  const baseUrl = getBOPBaseUrl(environment);
  const url = `${baseUrl}/shopper/${shopperId}/order/${orderNumber}/changeOrderOwner`;

  try {
    const response = await dispatchPostRequest({ url });
    trackAnonymousOrderTransferSuccess(metricsData);
    const data = await response.json();
    return data;
  } catch (err) {
    trackAnonymousOrderTransferError(err as object, metricsData);
    throw err;
  }
};

type TransferOrderParams = {
  orderNumber: string;
  shopperId: string;
  fromShopperId: boolean;
  environment: string;
};

export const transferOrders = async ({
  orderNumber,
  shopperId,
  fromShopperId,
  environment,
}: TransferOrderParams) => {
  const metricsData = {
    validationOrderNumber: orderNumber,
    shopperId,
    anonymousShopperId: fromShopperId,
    environment,
  };

  trackAnonymousOrderTransferRequested(metricsData);
  const baseUrl = getBOPBaseUrl(environment);
  const url = `${baseUrl}/shopper/${shopperId}/fromShopper/${fromShopperId}/order/${orderNumber}/transferOrdersAndAssets`;

  try {
    const response = await dispatchPostRequest({ url });
    trackAnonymousOrderTransferSuccess(metricsData);
    const data = await response.json();
    return data;
  } catch (err) {
    trackAnonymousOrderTransferError(err as object, metricsData);
    throw err;
  }
};
